

import {
  Component,
  ElementRef,
  OnInit,
  AfterViewInit,
  OnDestroy
} from '@angular/core';
import { CommonModule } from '@angular/common';

interface GbMiniStat {
  value: string;
  label: string;
}

interface GbTrustItem {
  icon: string;
  text: string;
}

interface GbPillar {
  num: string;
  tag: string;
  icon: string;
  colorClass: string;
  title: string;
  points: string[];
}

interface GbPtmItem {
  icon: string;
  title: string;
  desc: string;
}

interface GbOutcomeCard {
  icon: string;
  title: string;
  points: string[];
}

@Component({
  selector: 'app-gcse-bootcamp',
  imports: [CommonModule],
  templateUrl: './gcse-bootcamp.component.html',
  styleUrl: './gcse-bootcamp.component.css',
})
export class GcseBootcampComponent {

  boardChips: string[] = ['AQA', 'EDEXCEL', 'OCR', 'FOUNDATION & HIGHER'];

  miniStats: GbMiniStat[] = [
    { value: '3', label: 'Exam Boards Covered' },
    { value: '2', label: 'Tiers · Foundation & Higher' },
    { value: '100%', label: 'Syllabus, Every Topic' }
  ];

  trustItems: GbTrustItem[] = [
    { icon: 'ti-users-group', text: 'Small batches, individual attention' },
    { icon: 'ti-video', text: 'Live, interactive classes' },
    { icon: 'ti-report-analytics', text: 'Regular progress checks' },
    { icon: 'ti-message-circle-2', text: 'Parent-Teacher Meetings' },
    { icon: 'ti-target-arrow', text: 'Exam-board aligned technique' },
    { icon: 'ti-mood-smile', text: 'Built to reduce exam anxiety' }
  ];

  // rendered twice in the template for a seamless marquee loop
  trustItemsLoop: GbTrustItem[] = [...this.trustItems, ...this.trustItems];

  pillars: GbPillar[] = [
    {
      num: '01',
      tag: 'Coverage',
      icon: 'ti-books',
      colorClass: 'gb-pillar-1',
      title: 'Full syllabus, both tiers',
      points: [
        'Comprehensive revision of the complete GCSE syllabus',
        'Foundation and Higher level concepts both covered',
        'Content mapped to AQA, Edexcel, and OCR specifications'
      ]
    },
    {
      num: '02',
      tag: 'Technique',
      icon: 'ti-target-arrow',
      colorClass: 'gb-pillar-2',
      title: 'Technique that earns marks',
      points: [
        'Step-by-step methods for solving high-mark questions',
        'Dedicated time management and exam strategy sessions',
        'Intensive practice on the topics that trip students up most'
      ]
    },
    {
      num: '03',
      tag: 'Delivery',
      icon: 'ti-presentation',
      colorClass: 'gb-pillar-3',
      title: 'Live teaching, real practice',
      points: [
        'Live interactive classes led by experienced subject experts',
        'Topic-wise homework set after every single lesson',
        'Small batch sizes so nobody gets lost in the crowd'
      ]
    },
    {
      num: '04',
      tag: 'Feedback',
      icon: 'ti-chart-arrows-vertical',
      colorClass: 'gb-pillar-4',
      title: "Feedback that's specific",
      points: [
        'Regular quizzes and progress checks throughout',
        'Personalised feedback on strengths and weak spots',
        'Proven strategies aimed squarely at final-exam grades'
      ]
    }
  ];

  ptmItems: GbPtmItem[] = [
    {
      icon: 'ti-calendar-time',
      title: 'Regular PTMs',
      desc: 'Scheduled through the bootcamp, not just once at the end'
    },
    {
      icon: 'ti-clipboard-data',
      title: 'Detailed progress updates',
      desc: "What's been covered, what's improved, what still needs work"
    },
    {
      icon: 'ti-report',
      title: 'Performance reviews',
      desc: "An honest read on where your child stands against the target grade"
    },
    {
      icon: 'ti-route',
      title: 'Personalised action plans',
      desc: 'Clear next steps, tailored to your child, not a generic checklist'
    }
  ];

  outcomeCards: GbOutcomeCard[] = [
    {
      icon: 'ti-mood-smile',
      title: 'Built to reduce exam anxiety',
      points: [
        'Structured practice replaces last-minute panic',
        'Familiarity with real exam-style questions, well ahead of time'
      ]
    },
    {
      icon: 'ti-trophy',
      title: 'Focused on final-exam results',
      points: [
        'Every session ties back to maximising the final grade',
        'Strategies proven across past cohorts, not guesswork'
      ]
    }
  ];

  private observer: IntersectionObserver | null = null;

  constructor(private hostEl: ElementRef<HTMLElement>) {}

  ngOnInit(): void {
    this.loadFont(
      'gb-font-nunito',
      'https://fonts.googleapis.com/css2?family=Nunito:ital,wght@0,400;0,600;0,700;0,800;0,900;1,700&display=swap'
    );
    this.loadFont(
      'gb-font-tabler',
      'https://cdn.jsdelivr.net/npm/@tabler/icons-webfont@2.47.0/dist/tabler-icons.min.css'
    );

    // enables smooth scrolling for the in-page "#enrol" / "#inside" anchor links
    if (document && document.documentElement) {
      document.documentElement.style.scrollBehavior = 'smooth';
    }
  }

  ngAfterViewInit(): void {
    var self = this;
    var els = this.hostEl.nativeElement.querySelectorAll('.gb-reveal, .gb-stagger');

    if (!('IntersectionObserver' in window)) {
      els.forEach(function (el) {
        el.classList.add('is-visible');
      });
      return;
    }

    self.observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            if (self.observer) {
              self.observer.unobserve(entry.target);
            }
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );

    els.forEach(function (el) {
      if (self.observer) {
        self.observer.observe(el);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  private loadFont(id: string, href: string): void {
    if (document.getElementById(id)) {
      return;
    }
    var link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
  }

}
